import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { User } from '@mitel/cloudlink-sdk';
import { IntegSharedCCService } from '../../../../../core/services/integ-shared-cc.service';
import { OrdersService } from './../../../../../core/services/orders.service';
import { UserMitelOneSharedService } from '../../../../../core/services/user-mitel-one-shared.service';
import { AppStateService } from './../../../../../core/services/app-state.service';
import { XLaunchService } from './../../../../../core/services/x-launch.service';
import { mitelOneLicenses } from '../../../../../core/constants/constants';
import { rbacMode } from './../../../../../core/constants/constants';

@Component({
  selector: 'app-mitel-one-config',
  templateUrl: './mitel-one-config.component.html',
  styleUrls: ['./mitel-one-config.component.scss']
})
export class MitelOneConfigurationComponent implements OnInit {
  @Input() user: User;
  @Input() collapsed: boolean = false;  
  updateProdStateEvent = new Subject<any>();

  mitelOneForm = this.formBuilder.group({
    tierType: ['', [Validators.required]]
  });
  tierTypeOptions: any = {};
  showFormError = false;
  errMsgStyle: any = { 'max-width': '500px' };
  submitted = false;
  loading = true;
  previousPlan = '';
  promises: Promise<any>[] = [];
  setupComplete = false;
  rbacMode: string = '';
  
  isSubComponentDirty: boolean = false;

  constructor(private ordersService: OrdersService,
    private formBuilder: FormBuilder,
    private ccSharedService: IntegSharedCCService,
    private mitelOneSharedSvc: UserMitelOneSharedService,
    private appStateService: AppStateService,
    private xlaunchService: XLaunchService,
    private translateService: TranslateService
  ) { }

  async ngOnInit() {
    if (this.appStateService.getSupportPortalMode() || this.xlaunchService.isXLaunchedFromBoss()) {
      this.rbacMode = rbacMode.READONLY;
    }
    const remainingLicensePromise = this.ordersService.getRemainingLicenses()
      .then(remainingLicenses => {
        let licenses = {} as any;
        for (let license in remainingLicenses) {
          if (mitelOneLicenses.includes(license)) {
            licenses[license] = remainingLicenses[license];
          }
        }
        return licenses;
      }).catch(err => {
        console.log("Failed to fetch the available licenses");
        this.showFormError = true;
        return {};
      });
    this.addPromise(remainingLicensePromise);
    const userLicensesPromise = this.ordersService.getUserLicenses(this.user.userId)
      .then((userLicenses) => {
        const result = userLicenses.find(element => {
          return element.enable && mitelOneLicenses.includes(element.name);
        });
        if (!!result) {
          this.setupComplete = true;
          return result.name;
        }
        return '';
      })
      .catch((err) => {
        console.log('Failed to get user Licenses', err);
        return '';
      });
    this.addPromise(userLicensesPromise);
  }

  addPromise(promise: Promise<any>) {
    this.promises.push(promise);
    if (this.promises.length === 2) {
      Promise.all(this.promises).then((values) => {
        this.createTierTypeList(values[0], values[1]);
        this.loading = false;
        if (values[1]) {
          let plan = values[1];
          this.previousPlan = plan;
          this.setConfigurationForm(plan);
        }
      });
    }
  }

  createTierTypeList(licenseList: any, userLicense: string) {
    let licenseObj = {};
    for (let license in licenseList) {

        if (license === userLicense || (licenseList[license] > 0 )) {
          licenseObj[license] = { "available": licenseList[license] };
        }
        else if (licenseList[license] <= 0) {
            licenseObj[license] = { "available": licenseList[license], "reason": "mitelOne_config.no-licenses" };
        }
    }
    this.tierTypeOptions = licenseObj;
  }

  setConfigurationForm(selectedPlan: string) {
    this.mitelOneForm.controls['tierType'].patchValue(selectedPlan);
  }

  setCurrentTier(event: any) {
    if (event && event.value) {
      this.mitelOneForm.controls['tierType'].patchValue(event.value);
    }
  }

  selectTierType(type: string) {
    this.mitelOneForm.controls['tierType'].patchValue(type);
  }

  getCurrentLabelTier() {
    let currentTierValue = this.mitelOneForm.controls['tierType'].value;
    return currentTierValue ? this.translateService.instant('mitelOne_config.tier_type_options.' + currentTierValue) : '';
  }

  isSaveDisabled(): boolean {
    return !this.mitelOneForm || !this.mitelOneForm.controls || !this.mitelOneForm.controls['tierType'].valid ||
      (this.mitelOneForm.controls['tierType'].value === this.previousPlan && this.setupComplete) || this.submitted;
  }

  getUpdateProdStateEventConfigComp() {
    return this.updateProdStateEvent;
  }

  updateState(): void {
    this.updateProdStateEvent.next('updateState');
  }

  updateDirtyForm(dirty) {
    this.isSubComponentDirty = dirty;
  }

  isWarningVisible() {
    return (this.isSubComponentDirty && this.mitelOneForm.controls.tierType.value !== this.previousPlan) ? true : false;
  }

  async onSave() {
    this.submitted = true;
    this.showFormError = false;
    let value = this.mitelOneForm.controls['tierType'].value as string;
    let body: any = [
      {
        name: value,
        enable: true,
        count: 1
      }
    ];
    if (this.previousPlan) {
      body.push({
        name: this.previousPlan,
        enable: false,
        count: 1
      });
    }
    return this.ordersService.addOrRemoveMultipleUserLicense(this.user.userId, body)
      .then(async () => {
        this.ordersService.clearAcctSubscriptionsCache();
        this.setupComplete = await this.mitelOneSharedSvc.postLicenseChangeProcessing(this.user, body.length > 1);
        if (this.setupComplete) {
          this.previousPlan = body[0].name;
          this.submitted = false;
        }
        return this.setupComplete;
      })
      .catch((error: any) => {
        this.showFormError = true;
        this.submitted = false;
        console.log(`Failed to update user licenses ${error}`);
      });
  }

  onCancel() {
    this.mitelOneForm.controls['tierType'].patchValue(this.previousPlan);
  }

}
